import { c as _c } from "react/compiler-runtime";
import { Card, LinkButton } from '@onefootprint/ui';
import type React from 'react';
import { Fragment } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CardTitle } from '@/components/entity-onboarding-details';
import { useDecryptForm } from '@/entity/components/decrypt-form';
import type { WithEntityProps } from '@/entity/components/with-entity';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useField from '../../hooks/use-field';
import type { DiField } from '../../vault.types';
import Field from '../field';
export type FieldsetProps = WithEntityProps & {
  /** Shown in the title strip whenever Select all isn't. */
  action?: React.ReactNode;
  children?: React.ReactNode;
  count?: number;
  fields: DiField[];
  title: string;
};
const Fieldset = t0 => {
  const $ = _c(37);
  const {
    action,
    children,
    count,
    entity,
    fields,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const decryptControls = useDecryptControls();
  const decryptForm = useDecryptForm();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== decryptForm.control) {
    t3 = {
      control: decryptForm.control,
      name: "fields",
      defaultValue: t2
    };
    $[2] = decryptForm.control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const selectedFields = useWatch(t3);
  const dis = fields.map(_temp);
  const getFieldProps = useField(entity);
  let t4;
  if ($[4] !== getFieldProps) {
    t4 = di => getFieldProps(di).canSelect;
    $[4] = getFieldProps;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const selectableFields = dis.filter(t4);
  let t5;
  if ($[6] !== selectedFields) {
    t5 = field_0 => selectedFields.includes(field_0);
    $[6] = selectedFields;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const allSelected = selectableFields.every(t5);
  const hasSelectableFields = selectableFields.length > 0;
  const shouldShowSelectAll = decryptControls.step === "selecting-fields" && hasSelectableFields;
  let t6;
  if ($[8] !== decryptForm || $[9] !== selectableFields) {
    t6 = () => {
      decryptForm.setFields(selectableFields);
    };
    $[8] = decryptForm;
    $[9] = selectableFields;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleSelectAll = t6;
  let t7;
  if ($[11] !== decryptForm || $[12] !== selectableFields) {
    t7 = () => {
      decryptForm.unsetFields(selectableFields);
    };
    $[11] = decryptForm;
    $[12] = selectableFields;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleDeselectAll = t7;
  let t8;
  if ($[14] !== entity) {
    t8 = field_1 => {
      const {
        di: di_0,
        renderCustomField
      } = field_1;
      return renderCustomField ? <Fragment key={di_0}>{renderCustomField({
          entity,
          di: di_0
        })}</Fragment> : <Field key={di_0} di={di_0} entity={entity} />;
    };
    $[14] = entity;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const renderField = t8;
  let t9;
  if ($[16] !== action || $[17] !== allSelected || $[18] !== handleDeselectAll || $[19] !== handleSelectAll || $[20] !== shouldShowSelectAll || $[21] !== t) {
    t9 = shouldShowSelectAll ? <LinkButton variant="label-3" onClick={allSelected ? handleDeselectAll : handleSelectAll}>{allSelected ? t(_temp2) : t(_temp3)}</LinkButton> : action;
    $[16] = action;
    $[17] = allSelected;
    $[18] = handleDeselectAll;
    $[19] = handleSelectAll;
    $[20] = shouldShowSelectAll;
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== count || $[24] !== t9 || $[25] !== title) {
    t10 = <CardTitle as="h2" title={title} count={count} rightContent={t9} />;
    $[23] = count;
    $[24] = t9;
    $[25] = title;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== children || $[28] !== fields || $[29] !== renderField) {
    t11 = children || fields.map(renderField);
    $[27] = children;
    $[28] = fields;
    $[29] = renderField;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11) {
    t12 = <Card.Body className="flex-1 px-4 py-1.5">{t11}</Card.Body>;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t10 || $[34] !== t12 || $[35] !== title) {
    t13 = <Card.Root role="group" aria-label={title} className="flex h-full flex-col">{t10}{t12}</Card.Root>;
    $[33] = t10;
    $[34] = t12;
    $[35] = title;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
};
export default Fieldset;
function _temp(field) {
  return field.di;
}
function _temp2($) {
  return $.deselectAll;
}
function _temp3($_0) {
  return $_0.selectAll;
}